import React from 'react';
import { PageLayout } from '@/shared/layout';
import { useConstructionReportOverview } from './hooks/useConstructionReports';
import { useAuth } from '@/core/auth/AuthContext';
import { KPICard, Card, Button, Badge, Table, Divider } from '@/components/ui';
import { useNavigate } from 'react-router-dom';

export default function ConstructionReportOverviewPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading } = useConstructionReportOverview(profile?.company_id || '');

  const reportCategories = [
    { title: 'Tiến độ thi công', icon: 'ri-line-chart-line', path: './progress', color: 'bg-blue-500', desc: 'Theo dõi % hoàn thành, trễ hạn và khối lượng thực tế.' },
    { title: 'Chất lượng & Nghiệm thu', icon: 'ri-shield-check-line', path: './qa', color: 'bg-emerald-500', desc: 'Tỷ lệ pass/fail, lỗi NCR và nhật ký kiểm định.' },
    { title: 'Chi phí & Tài chính', icon: 'ri-money-dollar-circle-line', path: './cost', color: 'bg-amber-500', desc: 'Thực chi vs Ngân sách, chi phí vật tư và nhân công.' },
    { title: 'Vật tư & Kho vận', icon: 'ri-archive-line', path: './material', color: 'bg-indigo-500', desc: 'Tình hình sử dụng, hao hụt và tồn kho dự án.' },
    { title: 'Thiết bị & Máy móc', icon: 'ri-truck-line', path: './equipment', color: 'bg-slate-700', desc: 'Giờ máy hoạt động, hiệu suất khai thác và bảo trì.' },
    { title: 'Thanh toán & QS', icon: 'ri-bank-card-line', path: './payment', color: 'bg-purple-500', desc: 'Hồ sơ Payment Claim, công nợ và quyết toán đợt.' },
  ];

  return (
    <PageLayout
      title="Báo cáo Thi công Tổng thể"
      description="Trung tâm tổng hợp dữ liệu, phân tích hiệu năng và xuất báo cáo đa chiều cho dự án."
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-history-line">Lịch sử Snapshot</Button>
          <Button variant="primary" icon="ri-add-line">Tạo Snapshot mới</Button>
        </div>
      }
    >
      <div className="space-y-8">
        {/* Main KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tiến độ trung bình"
            value={stats?.overallProgress || 0}
            icon="ri-rocket-line"
            variant="primary"
            suffix=" %"
          />
          <KPICard
            title="Ngân sách sử dụng"
            value={((stats?.actualCost || 0) / (stats?.totalBudget || 1) * 100).toFixed(1)}
            icon="ri-pie-chart-line"
            variant="warning"
            suffix=" %"
          />
          <KPICard
            title="Sự cố đang mở"
            value={stats?.pendingIssues || 0}
            icon="ri-error-warning-line"
            variant="danger"
          />
          <KPICard
            title="QA Pass Rate"
            value={92.5}
            icon="ri-shield-user-line"
            variant="success"
            suffix=" %"
          />
        </div>

        <Divider />

        {/* Report Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportCategories.map((cat) => (
            <Card
              key={cat.path}
              className="p-6 hover:border-os-primary/50 transition-all cursor-pointer group flex flex-col h-full"
              onClick={() => navigate(cat.path)}
            >
              <div className={`w-12 h-12 rounded-2xl ${cat.color} text-white flex items-center justify-center mb-4  group-hover:scale-110 transition-transform`}>
                <i className={`${cat.icon} text-2xl`} />
              </div>
              <h3 className="font-normal text-slate-900 mb-2 group-hover:text-os-primary transition-colors">{cat.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed flex-1 italic">{cat.desc}</p>
              <div className="mt-4 pt-4 border-t border-slate-50 flex justify-between items-center text-os-primary">
                <span className="text-[10px] font-normal tracking-widest">Xem chi tiết</span>
                <i className="ri-arrow-right-line" />
              </div>
            </Card>
          ))}
        </div>

        {/* Recent Snapshots Section */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-normal text-slate-900 text-sm flex items-center gap-2">
              <i className="ri-camera-line text-os-primary" />
              Báo cáo Snapshot gần đây
            </h3>
            <Badge variant="neutral">Lưu trữ nội bộ</Badge>
          </div>
          <Table
            columns={[
              { key: 'name', title: 'Tên báo cáo' },
              { key: 'date', title: 'Ngày tạo' },
              { key: 'type', title: 'Loại', align: 'center' },
              { key: 'creator', title: 'Người tạo' },
              { key: 'actions', title: '', align: 'right' },
            ]}
            data={[
              { id: '1', name: 'Báo cáo Tuần 16 - Dự án Pine Hill', date: '2026-04-15', type: 'Weekly', creator: 'Nguyễn Văn A' },
              { id: '2', name: 'Báo cáo Tháng 03 - Tổng hợp Thi công', date: '2026-03-31', type: 'Monthly', creator: 'Trần Thị B' },
            ]}
            renderCell={(row, key) => {
              switch (key) {
                case 'name': return <span className="text-xs font-normal text-slate-700">{row.name}</span>;
                case 'date': return <span className="text-[10px] text-slate-400 font-mono italic">{row.date}</span>;
                case 'type': return <Badge variant="primary" className="text-[9px] font-normal">{row.type}</Badge>;
                case 'creator': return <span className="text-[10px] font-normal text-slate-500">{row.creator}</span>;
                case 'actions': return <Button variant="ghost" size="sm" icon="ri-download-line" />;
                default: return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
